import { CommonModule } from '@angular/common';
import { Component, ViewEncapsulation, ChangeDetectionStrategy, OnInit, inject, viewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { Store } from '@ngrx/store';
import { isEqual, orderBy } from 'lodash-es';
import { DatapointFieldListSelectors } from 'projects/semeris/src/store/datapoint-field-list/datapoint-field-list.selectors';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';

import { EditTemplateModalConfig, TemplateModalType } from './template-modal.models';
import { Permission } from '../../../shared/models/api/permissions.enum';
import { PermissionService } from '../../../shared/services/permission.service';
import { GridCell, optimizeGridSize } from '../../../shared/utils/grid-sizer';
import { ButtonDirective } from '../../../ui/button/button/button.directive';
import { CrudListItemMetaComponent } from '../../../unified-crud/components/crud-list-item-meta/crud-list-item-meta.component';
import { DatapointField } from '../../../unified-datapoint/models/datapoint-field.models';
import { DatapointRule, DatapointRuleType, isDatapointRule, RuleBase } from '../../../unified-datapoint/models/datapoint-rule.model';
import { TemplateFormComponent } from '../template-form/template-form.component';

@Component({
  imports: [CommonModule, CrudListItemMetaComponent, MatDialogModule, TemplateFormComponent, ButtonDirective],
  selector: 'semeris-edit-template-modal',
  templateUrl: './edit-template-modal.component.html',
  styleUrls: ['./edit-template-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditTemplateModalComponent implements OnInit {
  data = inject<EditTemplateModalConfig>(MAT_DIALOG_DATA);
  dialogRef = inject<MatDialogRef<EditTemplateModalComponent>>(MatDialogRef);
  private readonly permissionService = inject(PermissionService);
  private readonly store = inject(Store);

  public readonly templateForm = viewChild(TemplateFormComponent);

  public get ruleModalConfig() {
    const val = this.data;
    return val.type === TemplateModalType.RuleBase ? val : undefined;
  }

  public class$: Observable<string>;

  public datapointFieldOptions$: Observable<DatapointField[]> = this.store.select(DatapointFieldListSelectors.fields).pipe(
    map((fields) =>
      orderBy(fields, (f) => f.nameWithReference.toLocaleLowerCase()).map((field) => ({
        ...field,
        disabled: false,
        disabledOnList: false,
      })),
    ),
  );

  public ngOnInit(): void {
    this.class$ = this.permissionService.permissions$.pipe(
      distinctUntilChanged((a, b) => isEqual(a, b)),
      map((permissions: string[]) => {
        const result: string[] = [];

        const isWriteDatapointMeta = permissions.includes(Permission.WRITE_DATAPOINT_META);
        const isWriteDatapointMetaStats = permissions.includes(Permission.WRITE_DATAPOINT_META_STATS);
        const isTemplateUser = permissions.includes(Permission.TEMPLATE_USER);

        const cells: GridCell[] = [
          permissions.includes(Permission.WRITE_REFERENCE_ID) ? { size: 1, name: 'reference' } : undefined,
          permissions.includes(Permission.WRITE_INTEGRATION_IDENTIFIER) && this.data.hasIntegrationIdentifier
            ? { size: 1, name: 'integration-identifier' }
            : undefined,
          { size: 1, name: 'name' },
          this.data.hasDisplayShortName ? { size: 1, name: 'short-name' } : undefined,
          { size: 1, name: 'asset-class' },
          { size: 2, name: 'description' },
        ].filter((v) => !!v);

        if (this.data?.type === 'ruleBase') {
          cells.push({ size: 1, name: 'analyst-contribution' });

          if (isWriteDatapointMeta) {
            cells.push({ size: 1, name: 'ai-contribution' });
            cells.push({ size: 1, name: 'presentation' });
            cells.push({ size: 2, name: 'details' });
            cells.push({ size: 2, name: 'meta-value-description' });
            cells.push({ size: 1, name: 'meta-example' });
            cells.push({ size: 2, name: 'meta-value-issues' });
          }

          if (isWriteDatapointMetaStats) {
            cells.push({ size: 1, name: 'stats-enabled' });
            cells.push({ size: 2, name: 'stats-numeric-transform' });
            cells.push({ size: 1, name: 'stats-calculate-numeric' });
          }

          if (permissions.includes(Permission.WRITE_DATAPOINT_GT) && (this.data?.data as any)?.ruleType === 'Formula') {
            cells.push({ size: 1, name: 'ground-truth' });
          }

          if ((this.data?.data as DatapointRule)?.ruleType !== DatapointRuleType.LLM && !!(this.data?.data as DatapointRule)?.ruleType) {
            cells.push({ size: 1, name: 'expiration-period' });
          }
        }

        if (this.data?.type === TemplateModalType.Normal) {
          cells.push({ size: 1, name: 'topic-short-name' });
        }

        if (this.data?.type === TemplateModalType.Normal && permissions.includes(Permission.WRITE_CUSTOMIZE_BUILT_IN)) {
          cells.push({ size: 1, name: 'built-in' });
        }

        if (isTemplateUser) {
          result.push('has-template-controls');
        }

        cells.push(isTemplateUser ? { size: 3 } : { size: 1 });

        result.push(`grid-with-${optimizeGridSize(cells, [4, 5, 6, 7, 8, 9], [5]).numberOfRows}-rows`);

        return result.join(' ');
      }),
    );
  }

  public submit() {
    const result = this.templateForm().submit();

    if (result) {
      this.permissionService.permissions$.pipe(take(1)).subscribe((permissions: string[]) => {
        let finalResult: Partial<RuleBase> = { ...result };

        if (!permissions.includes(Permission.WRITE_DATAPOINT_META)) {
          finalResult = {
            ...finalResult,
            verificationType: undefined,
            presentationType: undefined,
            aiContribution: undefined,
            groundTruthDatapointField: undefined,
            meta: undefined,
          };
        }

        if (!permissions.includes(Permission.WRITE_DATAPOINT_META_STATS)) {
          finalResult = { ...finalResult, stats: undefined };
        }

        if (isDatapointRule(this.data?.data as RuleBase) && this.data.data.ruleType === DatapointRuleType.LLM) {
          finalResult = { ...finalResult, active: undefined };
        }

        this.dialogRef.close(finalResult);
      });
    }
  }
}
